import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FaChevronRight, FaFilter, FaTimes } from 'react-icons/fa';
import API_URL from '../../config/api';
import {
  buildFreshUrl,
  CONTENT_SYNC_KEYS,
  NO_CACHE_JSON_HEADERS,
  subscribeToContentUpdates,
} from '../../utils/contentSync';
import { buildProductPath } from '../../utils/productRoutes';
import { getRichTextPlainText, sanitizeRichTextHtml } from '../../utils/richText';
import { getPrimaryProductImage } from '../../utils/productMedia';
import pic1 from '../../Assets/pic1.jpg';
import pic2 from '../../Assets/pic2.jpg';
import pic3 from '../../Assets/pic3.jpg';
import pic4 from '../../Assets/pic4.jpg';
import './DemoProductPage.css';

const fallbackImages = [pic1, pic2, pic3, pic4];
const ALL_PRODUCTS_LABEL = 'All Products';

const normalizeCategoryWord = (word = '') => {
  if (word.length <= 3) {
    return word;
  }

  if (word.endsWith('ies')) {
    return `${word.slice(0, -3)}y`;
  }

  if (word.endsWith('ves')) {
    return word.slice(0, -1);
  }

  if (word.endsWith('s') && !word.endsWith('ss')) {
    return word.slice(0, -1);
  }

  return word;
};

const normalizeCategoryValue = (value = '') =>
  String(value)
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map(normalizeCategoryWord)
    .join(' ');

const isSameCategoryValue = (left = '', right = '') =>
  normalizeCategoryValue(left) === normalizeCategoryValue(right);

const normalizeSubcategorySource = (value) => {
  let source = value;

  if (typeof source === 'string') {
    try {
      const parsed = JSON.parse(source);
      source = Array.isArray(parsed) ? parsed : source.split(/\r\n|\r|\n|,/);
    } catch (_) {
      source = source.split(/\r\n|\r|\n|,/);
    }
  }

  if (!Array.isArray(source)) {
    return [];
  }

  return source
    .map((entry) => String(entry ?? '').trim())
    .filter(Boolean);
};

const formatCategoryLabel = (value = '') =>
  String(value || '')
    .split(/(\s+|&|\/|-|\(|\))/g)
    .map((part) => {
      if (!part || /^(\s+|&|\/|-|\(|\))$/.test(part)) {
        return part;
      }

      if (/^[A-Z0-9]{1,4}$/.test(part)) {
        return part;
      }

      return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
    })
    .join('')
    .replace(/\s+/g, ' ')
    .trim();

const normalizeCategoryStructure = (value) => {
  let source = value;

  if (typeof source === 'string') {
    try {
      source = JSON.parse(source);
    } catch (_) {
      source = null;
    }
  }

  if (!Array.isArray(source)) {
    return [];
  }

  return source
    .map((item) => {
      if (!item || typeof item !== 'object') {
        return null;
      }

      const displayName = String(item.displayName ?? item.name ?? item.title ?? '').trim();
      const rawKey = String(item.key ?? displayName).trim();
      const key = rawKey.toLowerCase().replace(/\s+/g, ' ');

      if (!displayName && !key) {
        return null;
      }

      return {
        key,
        displayName: displayName || formatCategoryLabel(key),
        subcategories: normalizeSubcategorySource(item.subcategories),
      };
    })
    .filter(Boolean);
};

const findMatchingCategory = (categories, value = '') =>
  categories.find(
    (category) =>
      isSameCategoryValue(category.key, value) ||
      isSameCategoryValue(category.displayName, value) ||
      (category.subcategories || []).some((subcategory) =>
        isSameCategoryValue(subcategory, value)
      )
  ) || null;

const findMatchingSubcategory = (category, value = '') =>
  (category?.subcategories || []).find((subcategory) =>
    isSameCategoryValue(subcategory, value)
  ) || null;

const getProductCategoryValues = (product) =>
  [product?.category, product?.subcategory]
    .map((value) => String(value ?? '').trim())
    .filter(Boolean);

const getPlainProductText = (value = '') => getRichTextPlainText(value).replace(/\s+/g, ' ').trim();

const DemoProductPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [activeCategory, setActiveCategory] = useState(ALL_PRODUCTS_LABEL);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [products, setProducts] = useState([]);
  const [categoryStructure, setCategoryStructure] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryLoading, setCategoryLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(buildFreshUrl(API_URL.PRODUCTS), {
          method: 'GET',
          cache: 'no-store',
          headers: {
            ...NO_CACHE_JSON_HEADERS,
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error ${response.status}`);
        }

        const result = await response.json();

        if (result.success && Array.isArray(result.products)) {
          setProducts(result.products);
        } else if (result.success && Array.isArray(result.data)) {
          setProducts(result.data);
        } else {
          setProducts([]);
          setError('No products found');
        }
      } catch (fetchError) {
        console.error('Fetch error:', fetchError);
        setError('Failed to fetch products');
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  useEffect(() => {
    let cancelled = false;

    const fetchCategoryStructure = async () => {
      try {
        setCategoryLoading(true);

        const response = await fetch(buildFreshUrl(API_URL.SITE_CONTENT), {
          cache: 'no-store',
          headers: {
            ...NO_CACHE_JSON_HEADERS,
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error ${response.status}`);
        }

        const result = await response.json();

        if (!cancelled && result.success) {
          setCategoryStructure(
            normalizeCategoryStructure(result.data?.product_category_slider)
          );
        }
      } catch (fetchError) {
        if (!cancelled) {
          console.error('Failed to fetch product page categories:', fetchError);
        }
      } finally {
        if (!cancelled) {
          setCategoryLoading(false);
        }
      }
    };

    fetchCategoryStructure();

    const unsubscribe = subscribeToContentUpdates({
      keys: [
        CONTENT_SYNC_KEYS.SITE_CONTENT,
        CONTENT_SYNC_KEYS.PRODUCT_CATEGORY_SLIDER,
      ],
      onUpdate: () => fetchCategoryStructure(),
      refreshOnFocus: false,
      refreshOnVisibilityChange: false,
      minIntervalMs: 0,
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    const search = searchParams.get('search');
    const category = searchParams.get('category');

    setSearchQuery(search ? decodeURIComponent(search) : '');
    setActiveCategory(category ? decodeURIComponent(category) : ALL_PRODUCTS_LABEL);
  }, [searchParams]);

  useEffect(() => {
    if (activeCategory === ALL_PRODUCTS_LABEL) {
      return;
    }

    const matchedCategory = findMatchingCategory(categoryStructure, activeCategory);

    if (!matchedCategory || matchedCategory.subcategories.length === 0) {
      return;
    }

    setExpandedCategories((previousValue) => {
      if (previousValue.has(matchedCategory.key)) {
        return previousValue;
      }

      const nextValue = new Set(previousValue);
      nextValue.add(matchedCategory.key);
      return nextValue;
    });
  }, [activeCategory, categoryStructure]);

  const toggleCategory = (categoryKey) => {
    setExpandedCategories((previousValue) => {
      const nextValue = new Set(previousValue);

      if (nextValue.has(categoryKey)) {
        nextValue.delete(categoryKey);
      } else {
        nextValue.add(categoryKey);
      }

      return nextValue;
    });
  };

  const activeCategoryMatch =
    activeCategory === ALL_PRODUCTS_LABEL
      ? null
      : findMatchingCategory(categoryStructure, activeCategory);
  const activeSubcategoryMatch = findMatchingSubcategory(
    activeCategoryMatch,
    activeCategory
  );

  const filteredProducts = products
    .filter((product) => {
      if (activeCategory === ALL_PRODUCTS_LABEL) {
        return true;
      }

      const productValues = getProductCategoryValues(product);

      if (activeSubcategoryMatch) {
        return productValues.some((value) =>
          isSameCategoryValue(value, activeSubcategoryMatch)
        );
      }

      if (activeCategoryMatch) {
        const matchesCategory = productValues.some(
          (value) =>
            isSameCategoryValue(value, activeCategoryMatch.key) ||
            isSameCategoryValue(value, activeCategoryMatch.displayName)
        );

        const matchesSubcategory = activeCategoryMatch.subcategories.some((subcategory) =>
          productValues.some((value) => isSameCategoryValue(value, subcategory))
        );

        return matchesCategory || matchesSubcategory;
      }

      return productValues.some((value) => isSameCategoryValue(value, activeCategory));
    })
    .filter((product) => {
      if (!searchQuery.trim()) {
        return true;
      }

      return getPlainProductText(product.name || '')
        .toLowerCase()
        .includes(searchQuery.toLowerCase());
    })
    .map((product, index) => ({
      id: product._id ?? product.id,
      slug: product.slug,
      name: product.name,
      image: getPrimaryProductImage(product, fallbackImages[index % fallbackImages.length]),
    }));

  const activeCategoryLabel =
    activeCategory === ALL_PRODUCTS_LABEL
      ? ALL_PRODUCTS_LABEL
      : activeSubcategoryMatch ||
        activeCategoryMatch?.displayName ||
        formatCategoryLabel(activeCategory);

  const handleProductClick = (product) => {
    navigate(buildProductPath(product));
  };

  return (
    <div className="panam-product-page">
      <div className="product-container">
        <div className="product-sidebar desktop-only">
          <div className="sidebar-header">
            <h3>PRODUCT CATEGORIES</h3>
          </div>
          <ul className="category-list">
            <li className="category-item">
              <div
                className={`category-link ${activeCategory === ALL_PRODUCTS_LABEL ? 'active' : ''}`}
                onClick={() => setActiveCategory(ALL_PRODUCTS_LABEL)}
              >
                <span className="category-name">All Products</span>
              </div>
            </li>
            {categoryStructure.map((categoryItem) => {
              const isCategoryActive =
                !activeSubcategoryMatch &&
                (isSameCategoryValue(activeCategory, categoryItem.key) ||
                  isSameCategoryValue(activeCategory, categoryItem.displayName));

              return (
                <li key={categoryItem.key || categoryItem.displayName} className="category-item">
                  <div
                    className={`category-link ${isCategoryActive ? 'active' : ''}`}
                    onClick={(event) => {
                      if (event.target.closest('.expand-arrow')) {
                        event.stopPropagation();
                        toggleCategory(categoryItem.key);
                        return;
                      }

                      setActiveCategory(categoryItem.key || categoryItem.displayName);
                      if (
                        categoryItem.subcategories.length > 0 &&
                        !expandedCategories.has(categoryItem.key)
                      ) {
                        toggleCategory(categoryItem.key);
                      }
                    }}
                  >
                    <span className="category-name">{categoryItem.displayName}</span>
                    {categoryItem.subcategories.length > 0 && (
                      <span
                        className={`expand-arrow ${
                          expandedCategories.has(categoryItem.key) ? 'expanded' : ''
                        }`}
                        onClick={(event) => {
                          event.stopPropagation();
                          toggleCategory(categoryItem.key);
                        }}
                      >
                        <FaChevronRight />
                      </span>
                    )}
                  </div>
                  {categoryItem.subcategories.length > 0 &&
                    expandedCategories.has(categoryItem.key) && (
                      <ul className="subcategory-list">
                        {categoryItem.subcategories.map((subcategory) => (
                          <li
                            key={`${categoryItem.key}-${subcategory}`}
                            className={`subcategory-item ${
                              isSameCategoryValue(activeCategory, subcategory) ? 'active' : ''
                            }`}
                            onClick={() => setActiveCategory(subcategory)}
                          >
                            {subcategory}
                          </li>
                        ))}
                      </ul>
                    )}
                </li>
              );
            })}
          </ul>
        </div>

        <div className="filter-button-container">
          <button className="filter-button" onClick={() => setIsFilterOpen(true)}>
            <FaFilter />
            <span>Filter</span>
          </button>
        </div>

        {isFilterOpen && <div className="filter-overlay" onClick={() => setIsFilterOpen(false)} />}

        <div className={`filter-sidebar ${isFilterOpen ? 'open' : ''}`}>
          <div className="filter-header">
            <h3>Product Categories</h3>
            <button className="close-filter" onClick={() => setIsFilterOpen(false)}>
              <FaTimes />
            </button>
          </div>

          <div className="filter-content">
            <div
              className={`category-link ${activeCategory === ALL_PRODUCTS_LABEL ? 'active' : ''}`}
              onClick={() => {
                setActiveCategory(ALL_PRODUCTS_LABEL);
                setIsFilterOpen(false);
              }}
            >
              All Products
            </div>
            {categoryStructure.map((categoryItem) => {
              const isCategoryActive =
                !activeSubcategoryMatch &&
                (isSameCategoryValue(activeCategory, categoryItem.key) ||
                  isSameCategoryValue(activeCategory, categoryItem.displayName));

              return (
                <div key={categoryItem.key || categoryItem.displayName}>
                  <div
                    className={`category-link ${isCategoryActive ? 'active' : ''}`}
                    onClick={(event) => {
                      if (event.target.closest('.expand-arrow')) {
                        event.stopPropagation();
                        toggleCategory(categoryItem.key);
                        return;
                      }

                      setActiveCategory(categoryItem.key || categoryItem.displayName);
                      setIsFilterOpen(false);
                      if (
                        categoryItem.subcategories.length > 0 &&
                        !expandedCategories.has(categoryItem.key)
                      ) {
                        toggleCategory(categoryItem.key);
                      }
                    }}
                  >
                    <span>{categoryItem.displayName}</span>
                    {categoryItem.subcategories.length > 0 && (
                      <span
                        className={`expand-arrow ${
                          expandedCategories.has(categoryItem.key) ? 'expanded' : ''
                        }`}
                        onClick={(event) => {
                          event.stopPropagation();
                          toggleCategory(categoryItem.key);
                        }}
                      >
                        <FaChevronRight />
                      </span>
                    )}
                  </div>
                  {categoryItem.subcategories.length > 0 &&
                    expandedCategories.has(categoryItem.key) && (
                      <div className="subcategory-list">
                        {categoryItem.subcategories.map((subcategory) => (
                          <div
                            key={`${categoryItem.key}-${subcategory}`}
                            className={`subcategory-item ${
                              isSameCategoryValue(activeCategory, subcategory) ? 'active' : ''
                            }`}
                            onClick={() => {
                              setActiveCategory(subcategory);
                              setIsFilterOpen(false);
                            }}
                          >
                            {subcategory}
                          </div>
                        ))}
                      </div>
                    )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="product-main">
          <div className="product-header">
            <div className="product-header-title-wrap">
              <h1 className="product-header-title">{activeCategoryLabel}</h1>
              <div className="product-header-accent" />
            </div>
            <p>{filteredProducts.length} products available</p>
          </div>

          <div className="product-grid">
            {loading || categoryLoading ? (
              <div className="loading-container">
                <div className="spinner"></div>
                <p>Loading products...</p>
              </div>
            ) : error ? (
              <div className="error-container">
                <p>{error}</p>
                <button
                  onClick={() => window.location.reload()}
                  className="retry-btn"
                >
                  Retry
                </button>
              </div>
            ) : filteredProducts.length > 0 ? (
              filteredProducts.map((product, index) => (
                <div
                  key={product.id}
                  className="product-card"
                  onClick={() => handleProductClick(product)}
                >
                  <div className="product-image">
                    <img
                      src={product.image}
                      alt={getRichTextPlainText(product.name) || 'Product'}
                      onError={(event) => {
                        event.target.src = fallbackImages[index % fallbackImages.length];
                      }}
                    />
                  </div>
                  <div
                    className="product-titlee"
                    dangerouslySetInnerHTML={{
                      __html: sanitizeRichTextHtml(product.name, { wrapPlainText: false }),
                    }}
                  />
                </div>
              ))
            ) : (
              <div className="no-products">
                <p>No products found.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemoProductPage;
